// servizi/notifiche.ts — v038: le notifiche («X ti sfida», «X vuole essere tuo amico», inviti a torneo e partita a
// 4). Sul sito: Web Push (service worker /sw-notifiche.js); nelle app: Firebase tramite il plugin di Capacitor.
// Le chiede solo chi ha l'account (le notifiche arrivano a una persona, non a un telefono anonimo).
import { richiesta } from "./api.ts";
import { leggiPreferenza, salvaPreferenza } from "./preferenze.ts";
import { PIATTAFORMA, PREFERENZE } from "../costanti.ts";
import { NATIVA } from "./nativo.ts";
import { segnalaErrore } from "./errori.ts";

/** v040 «errore»: permesso dato ma l'iscrizione non è riuscita (servizio di notifiche del telefono assente o giù). */
export type StatoNotifiche = "attive" | "spente" | "negate" | "non_supportate" | "errore";

const nativo = () => NATIVA;

export function notificheSupportate(): boolean {
  if (nativo()) return true;
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** Lo stato su questo telefono (senza chiedere niente). */
export function statoNotifiche(): StatoNotifiche {
  if (!notificheSupportate()) return "non_supportate";
  if (!nativo() && Notification.permission === "denied") return "negate";
  return leggiPreferenza(PREFERENZE.notifiche) === "si" ? "attive" : "spente";
}

const b64uInByte = (s: string): Uint8Array => {
  const b = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4));
  return Uint8Array.from(b, (c) => c.charCodeAt(0));
};
const byteInB64u = (b: ArrayBuffer | null): string | null =>
  b ? btoa(String.fromCharCode(...new Uint8Array(b))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") : null;

async function iscriviWeb(lingua: string): Promise<StatoNotifiche> {
  const permesso = await Notification.requestPermission();
  if (permesso !== "granted") return permesso === "denied" ? "negate" : "spente";
  const { chiave, attive } = await richiesta<{ chiave: string; attive: boolean }>("/api/v1/push/chiave");
  if (!attive) return "spente";
  const reg = await navigator.serviceWorker.register("/sw-notifiche.js");
  await navigator.serviceWorker.ready;
  let ab = await reg.pushManager.getSubscription();
  // chiave cambiata sul server: si rifà l'iscrizione
  if (ab && byteInB64u(ab.options.applicationServerKey ?? null) !== chiave) {
    await ab.unsubscribe().catch(() => undefined);
    ab = null;
  }
  ab ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64uInByte(chiave) as BufferSource });
  await richiesta("/api/v1/io/push", {
    metodo: "POST",
    corpo: { piattaforma: "web", indirizzo: ab.endpoint, p256dh: byteInB64u(ab.getKey("p256dh")), auth: byteInB64u(ab.getKey("auth")), lingua, fuso: fusoOrario() },
  });
  salvaPreferenza(PREFERENZE.notificheIndirizzo, ab.endpoint);
  return "attive";
}

/** v045: il fuso del telefono (la notifica «torna a giocare» arriva solo di giorno, nell'ora di chi la riceve). */
function fusoOrario(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

async function iscriviNativo(lingua: string): Promise<StatoNotifiche> {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  let p = await PushNotifications.checkPermissions();
  if (p.receive === "prompt" || p.receive === "prompt-with-rationale") p = await PushNotifications.requestPermissions();
  if (p.receive !== "granted") return "negate";
  const token = await new Promise<string>((ok, no) => {
    void PushNotifications.addListener("registration", (t) => ok(t.value));
    void PushNotifications.addListener("registrationError", (e) => no(new Error(String(e.error))));
    void PushNotifications.register();
    setTimeout(() => no(new Error("tempo")), 15_000);
  });
  await richiesta("/api/v1/io/push", { metodo: "POST", corpo: { piattaforma: PIATTAFORMA === "ios" ? "ios" : "android", indirizzo: token, lingua, fuso: fusoOrario() } });
  salvaPreferenza(PREFERENZE.notificheIndirizzo, token);
  // toccando la notifica si apre la pagina giusta
  void PushNotifications.addListener("pushNotificationActionPerformed", (a) => {
    const url = (a.notification.data as { url?: string } | undefined)?.url;
    if (url) {
      const u = new URL(url, location.origin);
      history.pushState(null, "", u.pathname + u.search);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  });
  return "attive";
}

/**
 * Accende le notifiche (chiede il permesso al telefono). v040: un errore non resta muto: torna «errore» (l'app lo
 * dice) e arriva nel pannello (Errori dell'app) con il motivo, per capire cosa succede su un telefono vero.
 */
export async function attivaNotifiche(lingua: string): Promise<StatoNotifiche> {
  if (!notificheSupportate()) return "non_supportate";
  let s: StatoNotifiche;
  try {
    s = nativo() ? await iscriviNativo(lingua) : await iscriviWeb(lingua);
  } catch (e) {
    segnalaErrore(new Error(`notifiche: ${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}`));
    s = !nativo() && Notification.permission === "denied" ? "negate" : "errore";
  }
  salvaPreferenza(PREFERENZE.notifiche, s === "attive" ? "si" : "no");
  return s;
}

/** v040: il telefono non ha ancora risposto alla domanda del permesso (né sì né no). */
export const permessoDaChiedere = (): boolean => !nativo() && notificheSupportate() && Notification.permission === "default";

export async function spegniNotifiche(): Promise<void> {
  salvaPreferenza(PREFERENZE.notifiche, "no");
  const indirizzo = leggiPreferenza(PREFERENZE.notificheIndirizzo);
  if (indirizzo) await richiesta("/api/v1/io/push", { metodo: "DELETE", corpo: { indirizzo } }).catch(() => undefined);
  salvaPreferenza(PREFERENZE.notificheIndirizzo, "");
  if (!nativo()) {
    const reg = await navigator.serviceWorker?.getRegistration("/sw-notifiche.js");
    await (await reg?.pushManager.getSubscription())?.unsubscribe().catch(() => undefined);
  }
}

/** All'apertura dell'app, con le notifiche accese: si rimanda l'iscrizione (il servizio può averla cambiata). */
export async function rinnovaNotifiche(lingua: string): Promise<void> {
  if (statoNotifiche() !== "attive") return;
  if (!nativo() && Notification.permission !== "granted") return;
  await attivaNotifiche(lingua).catch(() => undefined);
}

/** La domanda in Home si fa una volta («Non ora» = non si richiede più; si accendono dal profilo). */
export const domandaNotificheFatta = () => leggiPreferenza(PREFERENZE.notificheDomanda) === "si";
export const segnaDomandaNotifiche = () => salvaPreferenza(PREFERENZE.notificheDomanda, "si");
